import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

@Component({
    selector: 'app-amount-with-validation-renderer',
    templateUrl: './amount-with-validation-renderer.component.html',
    styleUrls: ['./amount-with-validation-renderer.component.scss'],
    standalone: false
})
export class AmountWithValidationRendererComponent implements ICellRendererAngularComp {
  value: string = '';
  params!: ICellRendererParams;
  showValidationIcon: boolean = false;
  placeHolder: string = '';
  maxLength: number = 20;
  isRequired: boolean = false;
  showValidator: boolean = false;
  cellClass: string = 'text-right';
  totalAmount: any;
  validationMessage: string = '';
  
  onInput: ((data: any) => void) | null = null;
  onFocus: ((data: any) => void) | null = null;
  onBlur: ((data: any) => void) | null = null;

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.value = params.value || '';
    
    // Get callback functions from cellRendererParams
    if (params.colDef && params.colDef.cellRendererParams) {
      this.onInput = params.colDef.cellRendererParams.onInput || null;
      this.onFocus = params.colDef.cellRendererParams.onFocus || null;
      this.onBlur = params.colDef.cellRendererParams.onBlur || null;
      this.cellClass = params.colDef.cellRendererParams.cssClass || 'text-right';
      
      // Get totalAmount from cellRendererParams (as a function)
      if (params.colDef.cellRendererParams.totalAmount) {
        const totalAmountFn = params.colDef.cellRendererParams.totalAmount;
        this.totalAmount = typeof totalAmountFn === 'function' ? totalAmountFn() : totalAmountFn;
      }
      
      // Get validation message from cellRendererParams
      if (params.colDef.cellRendererParams.validationMessage) {
        this.validationMessage = params.colDef.cellRendererParams.validationMessage;
      }
    }
    
    // Get placeholder from headerComponentParams
    if (params.colDef && params.colDef.headerComponentParams) {
      this.placeHolder = params.colDef.headerComponentParams.placeHolder || '';
      this.isRequired = params.colDef.headerComponentParams.isShowValidationOnInit || false;
    }
    
    this.updateValidationIcon();
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    this.value = params.value || '';
    
    // Update totalAmount from cellRendererParams
    if (params.colDef && params.colDef.cellRendererParams && params.colDef.cellRendererParams.totalAmount) {
      const totalAmountFn = params.colDef.cellRendererParams.totalAmount;
      this.totalAmount = typeof totalAmountFn === 'function' ? totalAmountFn() : totalAmountFn;
    }
    
    this.updateValidationIcon();
    return true;
  }

  updateValidationIcon() {
    // Show validation icon if entered amount exceeds total amount
    if (!this.value || !this.totalAmount) {
      this.showValidationIcon = false;
      return;
    }
    
    const enteredAmount = parseFloat(this.value);
    const total = parseFloat(this.totalAmount);
    
    // Check if both are valid numbers
    if (isNaN(enteredAmount) || isNaN(total)) {
      this.showValidationIcon = false;
      return;
    }
    
    this.showValidationIcon = enteredAmount > total;
  }

  handleInput(event: any) {
    this.value = event.target.value;
    
    // Get latest totalAmount value
    if (this.params && this.params.colDef && this.params.colDef.cellRendererParams && this.params.colDef.cellRendererParams.totalAmount) {
      const totalAmountFn = this.params.colDef.cellRendererParams.totalAmount;
      this.totalAmount = typeof totalAmountFn === 'function' ? totalAmountFn() : totalAmountFn;
    }
    
    this.updateValidationIcon();
    
    if (this.onInput && this.params) {
      this.onInput({
        event: event,
        rowData: this.params.data,
        value: this.value
      });
    }
  }

  handleFocus(event: any) {
    if (this.onFocus && this.params) {
      this.onFocus({
        event: event,
        rowData: this.params.data,
        value: this.value
      });
    }
  }

  handleBlur(event: any) {
    if (this.onBlur && this.params) {
      this.onBlur({
        event: event,
        rowData: this.params.data,
        value: this.value
      });
    }
    this.updateValidationIcon();
  }

  handleKeyDown(event: any) {
    // Allow arrow keys, tab, and other navigation keys to work
    const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'];
    if (allowedKeys.includes(event.key)) {
      event.stopPropagation();
    }
  }
}
